import { forwardRef, useState } from "react"
import {
  ArrowRight,
  Check,
  Crown,
  Info,
  Minus,
  Plus,
  AlertTriangle,
  RefreshCw,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { useBooking } from "@/context/BookingContext"
import { formatPriceDelta, getAvailableAddOns, addOnQuantityLimit } from "@/lib/booking"
import type { AddOnOption } from "@/lib/api/types"
import {
  getDisplayAddOns,
  getGroupedSelectionSummary,
  getGroupedTotalQuantity,
  isGroupedSelected,
} from "@/lib/addon-groups"
import type { GroupedAddOn } from "@/lib/addon-groups"
import { SectionHeader } from "./SectionHeader"
import { AddonInfoModal } from "./AddonInfoModal"
import { GroupedAddonModal } from "./GroupedAddonModal"
import { PackageSummary } from "./PackageSummary"
import { BratislavaBannerNotice } from "./BratislavaBannerNotice"
import { AddOnNoteMedia } from "./AddOnNoteMedia"

const BRATISLAVA_EVENT_ID = "76c8fb89-a392-459c-9ac7-56346e246079"
const SHUTTLE_TO_EVENT_NAMES = new Set([
  "Shuttle to Event - Vienna Airport -> Banska Bystrica",
  "Shuttle to Event - Vienna Airport -> Bratislava Airport -> Banska Bystrica",
])
const SHUTTLE_TO_AIRPORT_NAMES = new Set([
  "Shuttle to Airport - Banska Bystrica -> Vienna Airport",
  "Shuttle to Airport - Banska Bystrica -> Bratislava Airport -> Vienna Airport",
])

const DEFAULT_ADDON_DESCRIPTION = {
  selected: "Recommended extra for this package option.",
  optional: "",
}

function normalizeShuttleName(name: string | null): string {
  return (name ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[—–]/g, "-")
    .replace(/→/g, "->")
    .replace(/\s+/g, " ")
    .trim()
}

interface AddonsSectionProps {
  onContinue: () => void
  inlineSummaryRef?: (node: HTMLDivElement | null) => void
  showInlineSummary?: boolean
  onRetryAddOns?: () => void
}

export const AddonsSection = forwardRef<HTMLElement, AddonsSectionProps>(
  function AddonsSection(
    { onContinue, inlineSummaryRef, showInlineSummary = false, onRetryAddOns },
    ref
  ) {
    const { state, dispatch } = useBooking()
    const stepNumber =
      state.packageType === "flight-hotel"
        ? 3
        : state.packageType === "flight-only"
          ? 2
          : 2
    const addOns = getAvailableAddOns(state)
    const displayAddOns = getDisplayAddOns(state)
    const [infoAddOn, setInfoAddOn] = useState<AddOnOption | null>(null)
    const [groupedModal, setGroupedModal] = useState<GroupedAddOn | null>(null)

    function setQuantity(addOnId: string, quantity: number) {
      dispatch({ type: "SET_ADDON_QUANTITY", addOnId, quantity })
    }

    function handleGroupedApply(quantities: Record<string, number>) {
      for (const [id, qty] of Object.entries(quantities)) {
        dispatch({ type: "SET_ADDON_QUANTITY", addOnId: id, quantity: qty })
      }
    }

    return (
      <section ref={ref} className="scroll-mt-6 py-10">
        <AddonInfoModal
          open={!!infoAddOn}
          onOpenChange={(open) => {
            if (!open) setInfoAddOn(null)
          }}
          addOn={infoAddOn}
        />
        {groupedModal && (
          <GroupedAddonModal
            open={!!groupedModal}
            onOpenChange={(open) => {
              if (!open) setGroupedModal(null)
            }}
            grouped={groupedModal}
            selectedAddOns={state.selectedAddOns}
            maxTotal={(groupedModal.type ?? "").toLowerCase().includes("transfer") ? null : state.passengerCount}
            onApply={handleGroupedApply}
            contextNote={
              state.event?.id === BRATISLAVA_EVENT_ID &&
              state.selectedFlight?.outbound.destinationAirportCode === "BTS"
                ? SHUTTLE_TO_EVENT_NAMES.has(normalizeShuttleName(groupedModal.name))
                  ? "Pick up at Bratislava airport is approx. 1 hour after departure of the bus from Vienna airport."
                  : SHUTTLE_TO_AIRPORT_NAMES.has(normalizeShuttleName(groupedModal.name))
                  ? "Drop off at Bratislava airport is approx. 3hours after departure of the bus from Banská Bystrica."
                  : undefined
                : undefined
            }
          />
        )}
        <SectionHeader
          step={stepNumber}
          title="Level Up Your Trip"
          subtitle="Available extras before checkout"
        />

        {state.event?.id === BRATISLAVA_EVENT_ID &&
          state.selectedFlight?.outbound.destinationAirportCode === "BTS" && (
            <BratislavaBannerNotice />
          )}

        <div className="flex flex-col gap-4">
          {state.hotelAddOnsStatus === "loading" ? (
            <div className="rounded-[18px] border border-border bg-card/70 px-6 py-10 text-center text-sm text-muted-foreground">
              Loading add-ons…
            </div>
          ) : state.hotelAddOnsStatus === "error" ? (
            <div className="flex flex-col items-center gap-4 rounded-[18px] border border-border bg-card/80 px-6 py-12 text-center">
              <AlertTriangle className="size-8 text-amber-400" />
              <p className="text-sm text-muted-foreground">
                Add-ons could not be loaded.
              </p>
              {onRetryAddOns && (
                <Button variant="outline" className="gap-2" onClick={onRetryAddOns}>
                  <RefreshCw className="size-4" />
                  Try again
                </Button>
              )}
            </div>
          ) : addOns.length === 0 ? (
            <div className="rounded-[18px] border border-border bg-card/70 px-6 py-10 text-center text-sm text-muted-foreground">
              No add-ons are available for the selected package.
            </div>
          ) : (
            displayAddOns.map((display) => {
              if (display.kind === "single") {
                const addOn = display.addOn
                const quantity = state.selectedAddOns[addOn.id] ?? 0
                const selected = quantity > 0

                return (
                  <AddOnCard
                    key={addOn.id}
                    addOn={addOn}
                    selected={selected}
                    quantity={quantity}
                    locked={isIncludedMealAddOn(addOn)}
                    maxQuantity={addOnQuantityLimit(state, addOn.id)}
                    priceLabel={formatPriceDelta(
                      addOn.priceDeltaPerPerson,
                      state.currency
                    )}
                    onToggle={() => setQuantity(addOn.id, selected ? 0 : 1)}
                    onDecrease={() => setQuantity(addOn.id, quantity - 1)}
                    onIncrease={() => setQuantity(addOn.id, quantity + 1)}
                    onInfoClick={() => setInfoAddOn(addOn)}
                  />
                )
              }

              return (
                <GroupedAddOnCard
                  key={display.groupKey}
                  grouped={display}
                  selectedAddOns={state.selectedAddOns}
                  locked={isIncludedMealGroup(display)}
                  priceLabel={formatPriceDelta(
                    display.priceDeltaPerPerson,
                    state.currency
                  )}
                  onOpenModal={() => setGroupedModal(display)}
                />
              )
            })
          )}
        </div>

        {showInlineSummary && (
          <div ref={inlineSummaryRef} className="mt-10 @min-[64rem]:hidden">
            <div className="mb-3 flex items-center gap-3">
              <Separator className="flex-1" />
              <span className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
                Review &amp; Checkout
              </span>
              <Separator className="flex-1" />
            </div>
            <PackageSummary />
          </div>
        )}

        <div className="mt-6 flex @min-[64rem]:justify-end">
          <Button
            onClick={onContinue}
            type="button"
            size="xl"
            className="w-full @min-[64rem]:w-auto"
          >
            Continue to Checkout
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </section>
    )
  }
)

function GroupedAddOnCard({
  grouped,
  selectedAddOns,
  locked,
  priceLabel,
  onOpenModal,
}: {
  grouped: GroupedAddOn
  selectedAddOns: Record<string, number>
  locked: boolean
  priceLabel: string
  onOpenModal: () => void
}) {
  const selected = isGroupedSelected(grouped, selectedAddOns)
  const totalQuantity = getGroupedTotalQuantity(grouped, selectedAddOns)
  const selectionSummary = getGroupedSelectionSummary(grouped, selectedAddOns)
  const isVip = (grouped.type ?? "").toLowerCase().includes("vip")
  const apiIcon = getAddOnIconSource(grouped.icon)

  return (
    <div
      className={`w-full overflow-hidden rounded-[18px] border transition-all ${
        selected
          ? "border-primary shadow-sm"
          : "border-border/90 bg-card hover:border-border"
      }`}
      style={selected ? { background: "color-mix(in oklch, var(--primary) 8%, transparent)" } : undefined}
    >
      <div className="flex flex-1 flex-col justify-between gap-4 p-5 @min-[48rem]:flex-row @min-[48rem]:gap-6 @min-[48rem]:p-6">
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex items-start gap-4 @min-[48rem]:items-center">
            <div
              className={`flex size-12 shrink-0 items-center justify-center rounded-full backdrop-blur-sm transition-all @min-[48rem]:size-14 ${
                selected
                  ? "animate-addon-check bg-primary text-primary-foreground"
                  : "rounded-2xl bg-muted/50 text-primary"
              }`}
            >
              {selected ? (
                <Check className="size-6 @min-[48rem]:size-7" />
              ) : apiIcon ? (
                <img
                  src={apiIcon}
                  alt=""
                  className="size-6 object-contain @min-[48rem]:size-7"
                  loading="lazy"
                  aria-hidden="true"
                />
              ) : isVip ? (
                <Crown className="size-6" />
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-primary"
                >
                  <path
                    d="M3 11h18M7 11V8a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3M5 11v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>

            <div className="flex min-w-0 flex-col gap-1">
              <h3 className="text-[20px] leading-none font-bold text-foreground @min-[48rem]:text-[24px]">
                {grouped.name}
              </h3>
              <p className="text-[14px] text-muted-foreground @min-[48rem]:text-[15px]">
                {grouped.children.length} options available
              </p>
              {selectionSummary.length > 0 && (
                <div className="mt-1 flex flex-col gap-0.5">
                  {selectionSummary.map((item) => (
                    <span
                      key={item.note}
                      className="inline-flex flex-wrap items-center gap-x-1.5 text-[13px] font-medium text-primary"
                    >
                      <AddOnNoteMedia
                        note={item.note}
                        label={grouped.name}
                        thumbnailClassName="h-10 w-14"
                      />
                      ×{item.quantity}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-end justify-between gap-4 @min-[48rem]:flex-col @min-[48rem]:items-center @min-[48rem]:justify-end">
          <div className="min-h-10 flex-1 @min-[48rem]:hidden" />
          <div className="flex flex-col items-center gap-3">
            <div className="min-w-[120px] text-center @min-[48rem]:min-w-[140px]">
              <span
                className={`${
                  grouped.priceDeltaPerPerson === 0
                    ? "text-[15px] font-medium"
                    : "text-[22px] font-bold @min-[48rem]:text-[26px]"
                } leading-none text-primary`}
              >
                {priceLabel}
              </span>
            </div>

            {locked ? (
              <span className="inline-flex min-w-[120px] items-center justify-center gap-2 rounded-[14px] border border-primary/30 bg-primary/10 px-5 py-3 text-[15px] font-bold text-primary @min-[48rem]:min-w-[140px]">
                <Check className="size-4" />
                Included{totalQuantity > 1 ? ` ×${totalQuantity}` : ""}
              </span>
            ) : (
              <Button
                type="button"
                variant={selected ? "outline" : "default"}
                size="xl"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenModal()
                }}
                className={`min-w-[120px] gap-2 rounded-[14px] px-5 py-3 text-[15px] font-bold @min-[48rem]:min-w-[140px] ${
                  selected ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20" : ""
                }`}
              >
                {selected ? (
                  <>
                    <Info className="size-4" />
                    Edit selection{totalQuantity > 0 ? ` (${totalQuantity})` : ""}
                  </>
                ) : (
                  <>
                    <Plus className="size-4" />
                    Add
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function AddOnCard({
  addOn,
  selected,
  quantity,
  locked,
  maxQuantity,
  priceLabel,
  onToggle,
  onDecrease,
  onIncrease,
  onInfoClick,
}: {
  addOn: AddOnOption
  selected: boolean
  quantity: number
  locked: boolean
  maxQuantity: number
  priceLabel: string
  onToggle: () => void
  onDecrease: () => void
  onIncrease: () => void
  onInfoClick: () => void
}) {
  const isVip = (addOn.type ?? "").toLowerCase().includes("vip")
  const nameParts = getNameParts(addOn.name)
  const description =
    addOn.note ||
    (addOn.defaultSelected
      ? DEFAULT_ADDON_DESCRIPTION.selected
      : DEFAULT_ADDON_DESCRIPTION.optional)
  const apiIcon = getAddOnIconSource(addOn.icon)

  return (
    <div
      className={`w-full overflow-hidden rounded-[18px] border transition-all ${
        selected
          ? "border-primary shadow-sm"
          : "border-border/90 bg-card hover:border-border"
      }`}
      style={selected ? { background: "color-mix(in oklch, var(--primary) 8%, transparent)" } : undefined}
    >
      <div className="flex flex-1 flex-col justify-between gap-4 p-5 @min-[48rem]:flex-row @min-[48rem]:gap-6 @min-[48rem]:p-6">
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex items-start gap-4 @min-[48rem]:items-center">
            <div
              className={`flex size-12 shrink-0 items-center justify-center rounded-full backdrop-blur-sm transition-all @min-[48rem]:size-14 ${
                selected
                  ? "animate-addon-check bg-primary text-primary-foreground"
                  : "rounded-2xl bg-muted/50 text-primary"
              }`}
            >
              {selected ? (
                <Check className="size-6 @min-[48rem]:size-7" />
              ) : apiIcon ? (
                <img
                  src={apiIcon}
                  alt=""
                  className="size-6 object-contain @min-[48rem]:size-7"
                  loading="lazy"
                  aria-hidden="true"
                />
              ) : isVip ? (
                <Crown className="size-6" />
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-primary"
                >
                  <path
                    d="M3 11h18M7 11V8a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3M5 11v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>

            <div className="flex min-w-0 flex-col gap-1">
              <div>
                <h3 className="text-[20px] leading-tight font-bold text-foreground @min-[48rem]:text-[24px]">
                  {nameParts.prefix && `${nameParts.prefix} `}
                  <span className="inline-flex whitespace-nowrap align-baseline">
                    {nameParts.suffix}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation()
                        onInfoClick()
                      }}
                      aria-label={`More info about ${addOn.name}`}
                      className="-mt-1 ml-1 inline-flex size-6 align-middle rounded-full p-0"
                    >
                      <Info className="size-[18px] text-amber-400 transition-transform group-hover/info:scale-110" />
                    </Button>
                  </span>
                </h3>
              </div>
              {description && (
                <AddOnNoteMedia
                  note={description}
                  label={addOn.name}
                  className="text-[14px] text-muted-foreground @min-[48rem]:text-[15px]"
                />
              )}
            </div>
          </div>

        </div>

        <div className="flex items-end justify-between gap-4 @min-[48rem]:flex-col @min-[48rem]:items-center @min-[48rem]:justify-end">
          <div className="min-h-10 flex-1 @min-[48rem]:hidden" />
          <div className="flex flex-col items-center gap-3">
            <div className="min-w-[120px] text-center @min-[48rem]:min-w-[140px]">
              <span
                className={`${
                  addOn.priceDeltaPerPerson === 0
                    ? "text-[15px] font-medium"
                    : "text-[22px] font-bold @min-[48rem]:text-[26px]"
                } leading-none text-primary`}
              >
                {priceLabel}
              </span>
            </div>

            {locked ? (
              <span className="inline-flex min-w-[120px] items-center justify-center gap-2 rounded-[14px] border border-primary/30 bg-primary/10 px-5 py-3 text-[15px] font-bold text-primary @min-[48rem]:min-w-[140px]">
                <Check className="size-4" />
                Included{quantity > 1 ? ` ×${quantity}` : ""}
              </span>
            ) : maxQuantity === 1 ? (
              <Button
                type="button"
                variant={selected ? "outline" : "default"}
                size="xl"
                onClick={(e) => {
                  e.stopPropagation()
                  onToggle()
                }}
                className={`min-w-[120px] gap-2 rounded-[14px] px-5 py-3 text-[15px] font-bold @min-[48rem]:min-w-[140px] ${
                  selected ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20" : ""
                }`}
              >
                {selected ? (
                  <>
                    <Minus className="size-4" />
                    Remove
                  </>
                ) : (
                  <>
                    <Plus className="size-4" />
                    Add
                  </>
                )}
              </Button>
            ) : !selected ? (
              <Button
                type="button"
                variant="default"
                size="xl"
                onClick={(e) => {
                  e.stopPropagation()
                  onToggle()
                }}
                className="min-w-[120px] gap-2 rounded-[14px] px-5 py-3 text-[15px] font-bold @min-[48rem]:min-w-[140px]"
              >
                <Plus className="size-4" />
                Add
              </Button>
            ) : (
              <QuantityStepper
                quantity={quantity}
                max={maxQuantity}
                onDecrease={onDecrease}
                onIncrease={onIncrease}
                onRemove={onToggle}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function getNameParts(name: string | null) {
  const fallback = name?.trim() || "Add-on"
  const lastSpaceIndex = fallback.lastIndexOf(" ")

  if (lastSpaceIndex === -1) {
    return { prefix: "", suffix: fallback }
  }

  return {
    prefix: fallback.slice(0, lastSpaceIndex),
    suffix: fallback.slice(lastSpaceIndex + 1),
  }
}

function getAddOnIconSource(icon: string | null) {
  if (!icon) return null

  const normalizedIcon = icon.trim()

  if (!normalizedIcon) return null

  if (
    normalizedIcon.startsWith("http://") ||
    normalizedIcon.startsWith("https://") ||
    normalizedIcon.startsWith("/") ||
    normalizedIcon.startsWith("data:image/")
  ) {
    return normalizedIcon
  }

  return null
}

function isIncludedMealAddOn(addOn: AddOnOption) {
  return (
    (addOn.type ?? "").toUpperCase() === "MEAL" &&
    addOn.priceDeltaPerPerson === 0
  )
}

function isIncludedMealGroup(grouped: GroupedAddOn) {
  return (
    (grouped.type ?? "").toUpperCase() === "MEAL" &&
    grouped.priceDeltaPerPerson === 0
  )
}

function QuantityStepper({
  quantity,
  max,
  onDecrease,
  onIncrease,
  onRemove,
}: {
  quantity: number
  max: number
  onDecrease: () => void
  onIncrease: () => void
  onRemove: () => void
}) {
  return (
    <div className="animate-addon-controls flex items-center gap-2 rounded-full border border-border bg-popover px-2 py-2 shadow-md backdrop-blur-sm">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={(event) => {
          event.stopPropagation()
          if (quantity <= 1) onRemove()
          else onDecrease()
        }}
        className="size-8 rounded-full border border-border bg-muted/50 text-muted-foreground hover:bg-muted"
      >
        <Minus className="size-4" />
      </Button>

      <div className="min-w-8 text-center text-[15px] font-bold text-foreground">
        {quantity}
      </div>

      <Button
        type="button"
        size="icon"
        onClick={(event) => {
          event.stopPropagation()
          onIncrease()
        }}
        className="size-8 rounded-full"
        disabled={quantity >= max}
      >
        <Plus className="size-4" />
      </Button>
    </div>
  )
}
